import { z } from 'zod';
import { CURRENCIES, money } from './sales';

/**
 * Paket teklifler: a group or trade-fair programme shown to many customers through
 * one public link, and turned into a sale for each customer who takes it. Items are
 * written with the proposal item forms (proposalItemInput); a hotel adds what the
 * public page shows (packageHotelDisplay). Request schemas here; shapes in contract.ts.
 */

const id = z.uuid();
const version = z.number().int().positive();
const blankToNull = <T>(v: T) => (v === '' ? null : v);
const optional = (max: number) =>
  z.string().trim().max(max).nullable().optional().transform(blankToNull);
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarihi seçin')
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));
/** An empty field means "not shown", never zero. */
const optionalMoney = z.preprocess((v) => (v === '' ? null : v), money.nullable().optional());

const datesInOrder = {
  check: (p: { startDate?: string | null; endDate?: string | null }) =>
    !p.startDate || !p.endDate || p.endDate >= p.startDate,
  params: { path: ['endDate'], message: 'Bitiş tarihi başlangıçtan önce olamaz' },
};

const fields = {
  name: z.string().trim().min(1, 'Paketin adını yazın').max(160),
  eventName: optional(160),
  category: optional(60),
  description: optional(4000),
  startDate: day,
  endDate: day,
  currency: z.enum(CURRENCIES),
  /** One item per line; the public page lists them. */
  included: optional(4000),
  excluded: optional(4000),
  footnotes: optional(2000),
  /** For the agency only. */
  notes: optional(4000),
};

export const createPackageInput = z
  .object({ ...fields, currency: fields.currency.default('EUR') })
  .refine(datesInOrder.check, datesInOrder.params);
export type CreatePackageInput = z.input<typeof createPackageInput>;

export const updatePackageInput = z
  .object({ ...fields, active: z.boolean() })
  .partial()
  .extend({ version })
  .refine(datesInOrder.check, datesInOrder.params);
export type UpdatePackageInput = z.input<typeof updatePackageInput>;

/**
 * What the public page shows for a hotel beside the booking itself. Prices are in
 * the package's currency; an empty one is not shown.
 */
export const packageHotelDisplay = z.object({
  stars: z.number().int().min(1).max(5).nullable().optional(),
  distance: optional(80),
  doublePerPerson: optionalMoney,
  singlePackage: optionalMoney,
});
export type PackageHotelDisplayInput = z.input<typeof packageHotelDisplay>;

/** One customer takes the package: the items they chose become a sale. */
export const convertPackageInput = z
  .object({
    accountId: id.nullable().optional(),
    contactId: id.nullable().optional(),
    /** The company the sale is opened in (ADR 0017). */
    companyId: id.nullable().optional(),
    itemIds: z.array(id).min(1, 'Satışa aktarılacak en az bir kalem seçin').max(50),
    travellers: z.array(id).max(99).default([]),
    status: z.enum(['option', 'confirmed']).default('option'),
    optionDeadline: z.iso.datetime({ offset: true }).nullable().optional(),
    notes: optional(2000),
  })
  .refine((c) => c.accountId || c.contactId, {
    path: ['customer'],
    message: 'Bir firma ya da kişi seçin',
  })
  .refine((c) => c.status !== 'option' || c.optionDeadline, {
    path: ['optionDeadline'],
    message: 'Opsiyon son tarihini girin',
  });
export type ConvertPackageInput = z.input<typeof convertPackageInput>;

export const packageListQuery = z.object({
  q: z.string().trim().max(100).optional(),
  status: z.enum(['active', 'passive', 'all']).default('active'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
